
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DeleteClientCheck } from "@/models/Client";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";

export const useCanDeleteClient = (clientId: string) => {
  return useQuery({
    queryKey: ['canDeleteClient', clientId],
    queryFn: async (): Promise<DeleteClientCheck> => {
      const { data, error } = await supabase
        .rpc('can_delete_client', { client_id_param: clientId });

      if (error) {
        console.error('Error checking if client can be deleted:', error);
        throw error;
      }

      // Map snake_case response to camelCase interface
      const response = data[0];
      return {
        canDelete: response.can_delete,
        reason: response.reason,
        futureBookingsCount: response.future_bookings_count,
        activeSubscriptionsCount: response.active_subscriptions_count
      };
    },
    enabled: !!clientId
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();
  
  return useMutation({
    mutationFn: async (clientId: string) => {
      console.log('Deleting client:', clientId);

      const { error } = await supabase
        .from('clients')
        .update({ is_active: false })
        .eq('id', clientId);

      if (error) {
        console.error('Error deleting client:', error);
        throw error;
      }

      return clientId;
    },
    onSuccess: () => {
      // Invalidate all client-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['clients-paginated', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['clients-infinite', currentOrganization?.id] });
      // Also invalidate dashboard stats that depend on clients data
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-clients'] });
      toast.success('Client deleted successfully');
    },
    onError: (error) => {
      console.error('Error in delete client mutation:', error);
      toast.error('Failed to delete client');
    }
  });
};
